import React, { useState, useRef, createContext, useContext } from 'react';
import { Rnd } from 'react-rnd';
import { storage, saveTemplate, loadTemplates } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import BlockRenderer from '../BlockRenderer';
import { db } from '../../services/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useParams } from 'react-router-dom';

// TODO: import Firebase Storage and Firestore utils for upload/save

// Imgur upload helper
async function uploadToImgur(file) {
  const formData = new FormData();
  formData.append('image', file);
  const res = await fetch('https://api.imgur.com/3/image', {
    method: 'POST',
    headers: {
      Authorization: 'Client-ID YOUR_IMGUR_CLIENT_ID', // TODO: Replace with your Imgur Client-ID
    },
    body: formData,
  });
  const data = await res.json();
  if (data.success) {
    return data.data.link;
  } else {
    throw new Error('Imgur upload failed');
  }
}

function randomId() {
  return Math.random().toString(36).slice(2, 10);
}

const FONT_FAMILIES = [
  'Inter, Arial, sans-serif',
  'Roboto, Arial, sans-serif',
  'Georgia, serif',
  'Courier New, monospace',
  'Times New Roman, serif',
];

// Add custom handle styles
const handleStyles = {
  corner: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: '#fff',
    border: '3px solid #a259ff',
    boxShadow: '0 1px 4px rgba(162,89,255,0.15)',
    position: 'absolute',
    zIndex: 100,
    cursor: 'pointer',
  },
  side: {
    width: 12,
    height: 28,
    borderRadius: 4,
    background: '#fff',
    border: '2px solid #a259ff',
    boxShadow: '0 1px 4px rgba(162,89,255,0.10)',
    position: 'absolute',
    zIndex: 100,
    cursor: 'pointer',
  },
  rotate: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: '#fff',
    border: '2px solid #a259ff',
    boxShadow: '0 1px 4px rgba(162,89,255,0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    left: '50%',
    transform: 'translate(-50%, 0)',
    bottom: -48,
    zIndex: 101,
    cursor: 'grab',
  },
};

// Custom handle renderers for Rnd
function renderHandles(selected) {
  if (!selected) return {};
  return {
    topLeft: <div style={{ ...handleStyles.corner, top: -8, left: -8 }} />,
    top: <div style={{ ...handleStyles.side, top: -16, left: '50%', transform: 'translateX(-50%) rotate(90deg)' }} />,
    topRight: <div style={{ ...handleStyles.corner, top: -8, right: -8 }} />,
    right: <div style={{ ...handleStyles.side, top: '50%', right: -16, transform: 'translateY(-50%)' }} />,
    bottomRight: <div style={{ ...handleStyles.corner, bottom: -8, right: -8 }} />,
    bottom: <div style={{ ...handleStyles.side, bottom: -16, left: '50%', transform: 'translateX(-50%) rotate(90deg)' }} />,
    bottomLeft: <div style={{ ...handleStyles.corner, bottom: -8, left: -8 }} />,
    left: <div style={{ ...handleStyles.side, top: '50%', left: -16, transform: 'translateY(-50%)' }} />,
  };
}

// Defensive sanitize before saving
function sanitizeTabs(tabs) {
  const newTabs = {};
  for (const tabKey of Object.keys(tabs)) {
    const blocks = Array.isArray(tabs[tabKey]) ? tabs[tabKey] : [];
    newTabs[tabKey] = blocks
      .filter(Boolean)
      .map(block => {
        if (
          block &&
          block.type === 'button' &&
          (block.buttonType === 'weightSelector' || block.buttonType === 'beanTypeSelector')
        ) {
          return {
            ...block,
            options: Array.isArray(block.options) ? block.options : [],
            label: typeof block.label === 'string' ? block.label : '',
            unit: typeof block.unit === 'string' ? block.unit : '',
          };
        }
        return block;
      });
  }
  return newTabs;
}

// Utility to normalize 3-digit hex to 6-digit
function normalizeHexColor(hex) {
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    return (
      '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]
    );
  }
  return hex;
}

function PriceMatrixEditor({ prices, onChange, beanTypes, weights }) {
  const [localPrices, setLocalPrices] = React.useState(prices || {});
  React.useEffect(() => { setLocalPrices(prices || {}); }, [prices]);
  const handlePriceChange = (type, w, value) => {
    const updated = { ...localPrices, [type]: { ...localPrices[type], [w]: value } };
    setLocalPrices(updated);
    onChange(updated);
  };
  return (
    <table style={{ borderCollapse: 'collapse', marginBottom: 8 }}>
      <thead>
        <tr>
          <th style={{ border: '1px solid #ccc', padding: 4 }}></th>
          {weights.map(w => (
            <th key={w} style={{ border: '1px solid #ccc', padding: 4 }}>{w}g</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {beanTypes.map(type => (
          <tr key={type}>
            <td style={{ border: '1px solid #ccc', padding: 4, fontWeight: 500 }}>{type.charAt(0).toUpperCase() + type.slice(1)}</td>
            {weights.map(w => (
              <td key={w} style={{ border: '1px solid #ccc', padding: 4 }}>
                <input
                  type="number"
                  value={localPrices?.[type]?.[w] || ''}
                  onChange={e => handlePriceChange(type, w, e.target.value)}
                  placeholder={`Price for ${w}g`}
                  style={{ width: 80 }}
                />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Context to share selected weight between blocks in the same tab/layout
const SelectedWeightContext = createContext();

function WeightSelectorBlock({ block }) {
  const { selectedWeight, setSelectedWeight } = useContext(SelectedWeightContext);
  const options = block.options || [];
  const currentWeight = selectedWeight ?? (options[0] ? options[0].weight : null);
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        background: block.noButtonBackground ? 'transparent' : block.background,
        borderRadius: block.borderRadius,
        boxShadow: block.boxShadow,
        outline: block.outline,
        fontSize: block.fontSize,
        fontWeight: block.fontWeight,
        color: block.color,
      }}
    >
      <span>{block.label || 'Weight'}:</span>
      {options.filter(opt => opt.available).map(opt => {
        const buttonRadius = typeof block.buttonRadius === 'number' ? block.buttonRadius : 16;
        const isActive = currentWeight === opt.weight;
        return (
          <button
            key={opt.weight}
            style={{
              padding: '4px 12px',
              borderRadius: buttonRadius,
              backgroundColor: isActive ? '#222' : '#eee',
              color: isActive ? '#fff' : '#222',
              marginLeft: 4,
              borderWidth: isActive ? '2px' : '1px',
              borderStyle: 'solid',
              borderColor: isActive ? '#222' : '#ccc',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: block.fontSize,
              transition: 'all 0.2s',
              fontStyle: opt.default ? 'italic' : 'normal',
            }}
            onClick={() => setSelectedWeight(opt.weight)}
          >
            {opt.weight}{block.unit} {opt.price ? `(${opt.price} QAR)` : ''}
            {opt.default ? ' ★' : ''}
          </button>
        );
      })}
    </div>
  );
}

function DynamicPriceDisplayBlock({ block, weightSelectorBlock }) {
  const { selectedWeight } = useContext(SelectedWeightContext);
  const prices = weightSelectorBlock?.prices || {};
  const price = prices[selectedWeight] ?? '';
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontSize: block.fontSize,
        fontWeight: block.fontWeight,
        color: block.color,
        background: block.background,
        borderRadius: block.borderRadius,
        boxShadow: block.boxShadow,
        outline: block.outline,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      {price !== '' ? `${block.label || 'Price'}: ${price}` : (block.label || 'Price')}
    </div>
  );
}

function ProductEditorCanvas({ tabs, activeTab: propActiveTab, onTabsChange, onSave, productId: propProductId, type: productType, shopCategory }) {
  // Add this state for tab navigation FIRST
  const [activeTab, setActiveTab] = React.useState(propActiveTab || (productType === 'coffee' ? 'aboutBeans' : 'details'));
  const { productId: routeProductId } = useParams();
  const productId = propProductId || routeProductId;
  if (!productId) {
    console.warn('No productId found! Cannot save to Firestore.');
  }
  // Separate layouts for desktop and mobile
  const [isMobilePreview, setIsMobilePreview] = useState(false);
  const [desktopTabs, setDesktopTabs] = useState(() => tabs?.desktop || tabs || {});
  const [mobileTabs, setMobileTabs] = useState(() => tabs?.mobile || {});
  // Sync with parent tabs prop if it changes
  React.useEffect(() => {
    setDesktopTabs(tabs?.desktop || tabs || {});
    setMobileTabs(tabs?.mobile || {});
  }, [tabs]);
  // Use the correct tabs for the current mode
  const currentTabs = isMobilePreview ? mobileTabs : desktopTabs;
  const setCurrentTabs = isMobilePreview ? setMobileTabs : setDesktopTabs;
  // Only keep valid block objects with a type
  const blocks = Array.isArray(currentTabs?.[activeTab])
    ? currentTabs[activeTab].filter(b => b && typeof b === 'object' && typeof b.type === 'string')
    : [];
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSuccess, setTemplateSuccess] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [activeEditorTab, setActiveEditorTab] = useState('edit'); // 'edit' or 'templates'
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  // Clipboard for copy/paste
  const [clipboard, setClipboard] = useState(null);
  const [selectedWeight, setSelectedWeight] = useState();
  const selectedWeightState = { selectedWeight, setSelectedWeight };

  const filteredBlocks = (blocks || []).filter(Boolean);

  // Auto-initialize selectedWeight to the first available option of the first weight selector block
  React.useEffect(() => {
    const weightSelectorBlock = filteredBlocks.find(b => b.type === 'button' && b.buttonType === 'weightSelector');
    if (weightSelectorBlock && (!selectedWeight || !weightSelectorBlock.options?.some(opt => opt.weight === selectedWeight))) {
      const firstOption = weightSelectorBlock.options?.[0];
      if (firstOption) {
        setSelectedWeight(firstOption.weight);
      }
    }
    // eslint-disable-next-line
  }, [filteredBlocks]);

  const selectedBlock = filteredBlocks.find(b => b.id === selectedBlockId);

  // --- Block Editing Actions ---
  // Always update the correct layout state (desktopTabs or mobileTabs)
  const updateBlocks = (newBlocks) => {
    if (isMobilePreview) {
      setMobileTabs(prev => ({ ...prev, [activeTab]: newBlocks }));
      onTabsChange({ desktop: desktopTabs, mobile: { ...mobileTabs, [activeTab]: newBlocks } });
    } else {
      setDesktopTabs(prev => ({ ...prev, [activeTab]: newBlocks }));
      onTabsChange({ desktop: { ...desktopTabs, [activeTab]: newBlocks }, mobile: mobileTabs });
    }
  };

  const handleBlockChange = (id, data) => {

    const newBlocks = blocks.map(block =>
      block.id === id ? { ...block, ...data } : block
    );
    updateBlocks(newBlocks);
  };

  const handleAddBlock = async (type) => {
    let newBlock;
    // Canvas size for centering
    const canvasWidth = isMobilePreview ? 375 : 1300;
    const canvasHeight = isMobilePreview ? 667 : 800;
    if (type === 'text') {
      const width = 180;
      const height = 40;
      newBlock = {
        id: randomId(),
        type: 'text',
        content: 'New Text',
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        fontSize: 20,
        fontWeight: 'bold',
        color: '#111',
        fontFamily: FONT_FAMILIES[0],
        letterSpacing: 0,
        lineHeight: 1.2,
        textAlign: 'left',
        textShadow: '',
        outline: '',
        rotation: 0,
        wrapText: true,
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
    } else if (type === 'image') {
      const url = prompt('Enter Imgur image URL (must start with https://i.imgur.com/)');
      if (!url || !/^https:\/\/i\.imgur\.com\//.test(url)) {
        alert('Please enter a valid Imgur image URL (must start with https://i.imgur.com/)');
        return;
      }
      const width = 200;
      const height = 200;
      newBlock = {
        id: randomId(),
        type: 'image',
        src: url,
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        borderRadius: 0,
        boxShadow: '',
        outline: '',
        opacity: 1,
        rotation: 0,
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
      return;
    } else if (type === 'button') {
      const width = 120;
      const height = 48;
      newBlock = {
        id: randomId(),
        type: 'button',
        buttonType: 'addToCart', // default
        content: 'ADD TO CART',
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        fontSize: 18,
        fontWeight: 700,
        color: '#fff',
        background: '#222',
        borderRadius: 8,
        boxShadow: '',
        outline: '',
        rotation: 0,
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
    } else if (type === 'weightSelector') {
      const width = 220;
      const height = 48;
      newBlock = {
        id: randomId(),
        type: 'button',
        buttonType: 'weightSelector',
        label: 'Weight',
        options: [
          { weight: 250, price: 55, available: true, default: true, sku: 'DR-250' },
          { weight: 1000, price: 180, available: true, default: false, sku: 'DR-1KG' }
        ],
        unit: 'g',
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        fontSize: 18,
        fontWeight: 700,
        color: '#222',
        background: '#f7f7f7',
        backgroundOpacity: 1,
        borderRadius: 8,
        buttonRadius: 16,
        boxShadow: '',
        outline: '',
        rotation: 0,
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
    } else if (type === 'typeSelector') {
      const width = 220;
      const height = 48;
      newBlock = {
        id: randomId(),
        type: 'button',
        buttonType: 'typeSelector',
        label: 'Type',
        options: [
          { type: 'filter', label: 'Filter', available: true },
          { type: 'espresso', label: 'Espresso', available: true }
        ],
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        fontSize: 18,
        fontWeight: 700,
        color: '#222',
        background: '#f7f7f7',
        borderRadius: 8,
        boxShadow: '',
        outline: '',
        rotation: 0,
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
    } else if (type === 'display') {
      const width = 120;
      const height = 48;
      newBlock = {
        id: randomId(),
        type: 'display',
        displayType: 'quantity', // default
        label: 'Quantity',
        unit: 'g',
        gramsPerUnit: 250,
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        fontSize: 18,
        fontWeight: 700,
        color: '#222',
        background: '#f7f7f7',
        borderRadius: 8,
        boxShadow: '',
        outline: '',
        rotation: 0,
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
    } else if (type === 'video') {
      const url = prompt('Enter Imgur video URL (must start with https://i.imgur.com/ and end with .mp4, .webm, or .gifv)');
      if (!url || !/^https:\/\/i\.imgur\.com\/.*\.(mp4|webm|gifv)$/.test(url)) {
        alert('Please enter a valid Imgur video URL (must start with https://i.imgur.com/ and end with .mp4, .webm, or .gifv)');
        return;
      }
      const width = 320;
      const height = 180;
      newBlock = {
        id: randomId(),
        type: 'video',
        src: url,
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        borderRadius: 0,
        boxShadow: '',
        outline: '',
        opacity: 1,
        rotation: 0,
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
      return;
    } else if (type === 'pricing') {
      const width = 180;
      const height = 40;
      newBlock = {
        id: randomId(),
        type: 'pricing',
        x: Math.round((canvasWidth - width) / 2),
        y: Math.round((canvasHeight - height) / 2),
        width,
        height,
        fontSize: 28,
        fontWeight: 700,
        color: '#111',
        background: 'rgba(255,255,255,0.85)',
        borderRadius: 8,
        boxShadow: '',
        outline: '',
        rotation: 0,
        textAlign: 'center',
      };
      updateBlocks([...blocks, newBlock]);
      setSelectedBlockId(newBlock.id);
    }
  };

  const handleDeleteBlock = (id) => {
    const newBlocks = blocks.filter(block => block.id !== id);
    updateBlocks(newBlocks);
    setSelectedBlockId(null);
  };

  const handleRotateBlock = (id, delta) => {
    const block = blocks.find(b => b.id === id);
    if (!block) return;
    const newRotation = ((block.rotation || 0) + delta + 360) % 360;
    handleBlockChange(id, { rotation: newRotation });
  };

  // --- Properties Panel ---
  const handlePropChange = (prop, value) => {

    if (!selectedBlock) return;
    handleBlockChange(selectedBlock.id, { [prop]: value });
  };

  const handleSaveTemplate = async () => {
    const name = prompt('Template name?');
    if (!name) return;
    setTemplateLoading(true);
    try {
      await saveTemplate(name, activeTab, blocks);
      setTemplateSuccess(true);
      setTimeout(() => setTemplateSuccess(false), 2000);
    } catch (e) {
      alert('Failed to save template: ' + e.message);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Save both layouts and legacy tabs for compatibility
      const sanitizedDesktopTabs = sanitizeTabs(desktopTabs);
      const sanitizedMobileTabs = sanitizeTabs(mobileTabs);
      // Filter out malformed typeSelector options
      function filterTypeSelectorOptions(tabs) {
        const newTabs = {};
        for (const [tabKey, blocks] of Object.entries(tabs)) {
          newTabs[tabKey] = (blocks || []).map(block => {
            if (
              block &&
              block.type === 'button' &&
              block.buttonType === 'typeSelector' &&
              Array.isArray(block.options)
            ) {
              return {
                ...block,
                options: block.options.filter(opt =>
                  opt && typeof opt.type === 'string' && opt.type.trim() &&
                  typeof opt.label === 'string' && opt.label.trim()
                ),
              };
            }
            return block;
          });
        }
        return newTabs;
      }
      const filteredDesktopTabs = filterTypeSelectorOptions(sanitizedDesktopTabs);
      const filteredMobileTabs = filterTypeSelectorOptions(sanitizedMobileTabs);
      // Extract beanTypes from typeSelector in desktopTabs
      function extractBeanTypesFromTabs(tabs) {
        for (const tabBlocks of Object.values(tabs)) {
          if (Array.isArray(tabBlocks)) {
            const typeSelector = tabBlocks.find(
              b => b.type === 'button' && b.buttonType === 'typeSelector'
            );
            if (typeSelector && Array.isArray(typeSelector.options)) {
              return typeSelector.options
                .filter(opt => opt.available)
                .map(opt => opt.type)
                .filter(Boolean);
            }
          }
        }
        return [];
      }
      const beanTypes = extractBeanTypesFromTabs(filteredDesktopTabs);
      await setDoc(
        doc(db, 'products', productId),
        {
          tabs: filteredDesktopTabs, // for backward compatibility
          desktopTabs: filteredDesktopTabs,
          mobileTabs: filteredMobileTabs,
          beanTypes // always set from typeSelector
        },
        { merge: true }
      );
      alert('Saved!');
    } catch (e) {
      alert('Save failed: ' + e.message);
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const loaded = await loadTemplates(activeTab);
      setTemplates(loaded);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Fetch templates when switching to the templates tab or changing tab
  React.useEffect(() => {
    if (activeEditorTab === 'templates') fetchTemplates();
    // eslint-disable-next-line
  }, [activeEditorTab, activeTab]);

  const handleSaveTemplatePlacements = async () => {
    const name = prompt('Template name?');
    if (!name) return;
    setTemplateLoading(true);
    try {
      // Save all blocks (including images and videos)
      await saveTemplate(name, activeTab, blocks);
      setTemplateSuccess(true);
      fetchTemplates();
      setTimeout(() => setTemplateSuccess(false), 2000);
    } catch (e) {
      alert('Failed to save template: ' + e.message);
    } finally {
      setTemplateLoading(false);
    }
  };

  // Delete a template by ID
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    setTemplateLoading(true);
    try {
      const { doc, db } = await import('../../services/firebase');
      await (await import('firebase/firestore')).deleteDoc(doc(db, 'templates', templateId));
      fetchTemplates();
    } catch (e) {
      alert('Failed to delete template: ' + e.message);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleLoadTemplate = (templateBlocks) => {
    // Restore all blocks from template (including images and videos)
    updateBlocks(templateBlocks);
  };

  // Add visible error if productId is missing
  const productIdError = !productId;

  // Copy selected block to clipboard
  const handleCopyBlock = () => {
    if (!selectedBlock) return;
    setClipboard(JSON.parse(JSON.stringify(selectedBlock)));
  };

  // Paste block from clipboard to current layout/tab
  const handlePasteBlock = () => {
    if (!clipboard) return;
    // Deep copy and assign new ID and offset position
    const newBlock = {
      ...JSON.parse(JSON.stringify(clipboard)),
      id: randomId(),
      x: (clipboard.x || 0) + 40,
      y: (clipboard.y || 0) + 40,
    };
    updateBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  const handleClearTab = () => {
    setMobileTabs({ ...mobileTabs, [activeTab]: [] });
    onTabsChange({ desktop: desktopTabs, mobile: { ...mobileTabs, [activeTab]: [] } });
    setSelectedBlockId(null);
  };

  // Determine if this is a non-coffee product
  const isNonCoffee = (productType && productType !== 'coffee') || (shopCategory && shopCategory !== 'coffee');

  // Tabs logic
  const allowedTabs = isNonCoffee
    ? { details: currentTabs.details || [] }
    : currentTabs;
  const allowedTabKeys = isNonCoffee ? ['details'] : Object.keys(currentTabs);

  // When adding a new product or changing type to non-coffee, auto-set tabs to { details: [] }
  React.useEffect(() => {
    if (isNonCoffee && Object.keys(currentTabs).length !== 1) {
      setCurrentTabs({ details: currentTabs.details || [] });
      onTabsChange({ ...currentTabs, details: currentTabs.details || [] });
      setActiveTab('details');
    }
  }, [isNonCoffee]);

  // Sync local activeTab with propActiveTab
  React.useEffect(() => {
    setActiveTab(propActiveTab || (productType === 'coffee' ? 'aboutBeans' : 'details'));
  }, [propActiveTab, productType]);

  return (
    <div style={{ width: '100%', minHeight: isMobilePreview ? 700 : 800, margin: '32px auto', transition: 'width 0.3s, min-height 0.3s' }}>
      {/* Layout toggle */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <span style={{ fontWeight: 600 }}>Layout:</span>
        <button
          onClick={() => setIsMobilePreview(false)}
          style={{
            fontWeight: !isMobilePreview ? 700 : 400,
            background: !isMobilePreview ? '#a259ff' : '#eee',
            color: !isMobilePreview ? '#fff' : '#222',
            border: '1px solid #ccc',
            borderRadius: 6,
            padding: '4px 18px',
            cursor: 'pointer',
          }}
        >
          Desktop
        </button>
        <button
          onClick={() => setIsMobilePreview(true)}
          style={{
            fontWeight: isMobilePreview ? 700 : 400,
            background: isMobilePreview ? '#a259ff' : '#eee',
            color: isMobilePreview ? '#fff' : '#222',
            border: '1px solid #ccc',
            borderRadius: 6,
            padding: '4px 18px',
            cursor: 'pointer',
          }}
        >
          Mobile
        </button>
        <span style={{ marginLeft: 16, color: '#888', fontWeight: 400, fontSize: 15 }}>
          Editing: <b>{isMobilePreview ? 'Mobile' : 'Desktop'}</b>
        </span>
        {isMobilePreview && (
          <button
            onClick={handleClearTab}
            style={{ marginLeft: 24, background: '#e00', color: '#fff', fontWeight: 700, borderRadius: 6, padding: '4px 18px', border: 'none', cursor: 'pointer' }}
          >
            Clear Tab
          </button>
        )}
      </div>
      {/* Tab selector */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 16,
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        background: '#fff',
      }}>
        <button onClick={() => setActiveEditorTab('edit')} style={{ fontWeight: activeEditorTab === 'edit' ? 700 : 400 }}>Edit</button>
        <button onClick={() => setActiveEditorTab('templates')} style={{ fontWeight: activeEditorTab === 'templates' ? 700 : 400 }}>Templates</button>
      </div>
      {activeEditorTab === 'templates' ? (
        <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 24, minHeight: 300 }}>
          <button onClick={handleSaveTemplatePlacements} disabled={templateLoading} style={{ background: '#a259ff', color: '#fff', fontWeight: 600, marginBottom: 16 }}>
            {templateLoading ? 'Saving...' : 'Save Current Layout as Template'}
          </button>
          {templateSuccess && <span style={{ color: '#0a0', fontWeight: 600, marginLeft: 8 }}>Template saved!</span>}
          <h4>Saved Templates</h4>
          {loadingTemplates ? <div>Loading templates...</div> : (
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {templates.map(t => (
                <li key={t.id} style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 500 }}>{t.name}</span>
                  <button onClick={() => handleLoadTemplate(t.blocks)} style={{ fontSize: 13, padding: '2px 10px' }}>Load</button>
                  <button onClick={() => handleDeleteTemplate(t.id)} style={{ fontSize: 13, padding: '2px 10px', color: '#e00', border: '1px solid #e00', background: '#fff' }}>Delete</button>
                </li>
              ))}
              {templates.length === 0 && <li>No templates saved yet.</li>}
            </ul>
          )}
        </div>
      ) : (
        <>
          {productIdError && (
            <div style={{ color: '#e00', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>
              Error: No productId found! Cannot save to Firestore. Please navigate from the product list or ensure a valid product is selected.
            </div>
          )}
          {/* Add block controls */}
          <div style={{
            display: 'flex',
            gap: 12,
            marginBottom: 16,
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            background: '#fff',
          }}>
            <button onClick={() => handleAddBlock('text')}>Add Text</button>
            <button onClick={() => handleAddBlock('image')}>Add Image</button>
            <button onClick={() => handleAddBlock('button')}>Add Button</button>
            <button onClick={() => handleAddBlock('weightSelector')}>Add Weight Selector</button>
            <button onClick={() => handleAddBlock('typeSelector')}>Add Type Selector</button>
            <button onClick={() => handleAddBlock('display')}>Add Display</button>
            <button onClick={() => handleAddBlock('video')}>Add Video</button>
            <button onClick={() => handleAddBlock('pricing')}>Add Pricing</button>
            <button onClick={() => handleAddBlock('weightSelector')}>Add Weight Selector</button>
            <button onClick={() => handleAddBlock('typeSelector')}>Add Type Selector</button>
            <button onClick={() => handleAddBlock('display')}>Add Display</button>
            <button onClick={() => handleAddBlock('video')}>Add Video</button>
            <button onClick={handleSave} disabled={templateLoading || productIdError} style={{ background: '#a259ff', color: '#fff', fontWeight: 600, opacity: productIdError ? 0.5 : 1 }}>
              {templateLoading ? 'Saving...' : 'Save as Template'}
            </button>
            {templateSuccess && <span style={{ color: '#0a0', fontWeight: 600, marginLeft: 8 }}>Template saved!</span>}
            <button onClick={() => setIsPreviewMode(p => !p)} style={{ marginLeft: 24, background: isPreviewMode ? '#222' : '#eee', color: isPreviewMode ? '#fff' : '#222', fontWeight: 700, borderRadius: 6, padding: '0 18px' }}>
              {isPreviewMode ? 'Exit Preview' : 'Preview'}
            </button>
          </div>
          <div style={
            isMobilePreview
              ? {
                  width: 340,
                  maxWidth: '95vw',
                  height: 600,
                  borderRadius: 0,
                  background: 'rgba(255,255,255,0.95)',
                  boxShadow: '0px 4px 24px rgba(0,0,0,0.1)',
                  padding: 16,
                  border: '1px solid #000',
                  overflow: 'visible',
                  boxSizing: 'border-box',
                  margin: '0 auto',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  transition: 'width 0.3s, height 0.3s, border-radius 0.3s',
                }
              : {
                  position: 'relative',
                  width: 1300,
                  height: 800,
                  background: '#fff',
                  border: '1px solid #ccc',
                  margin: '0 auto',
                  borderRadius: 8,
                  overflow: 'hidden',
                  transition: 'width 0.3s, height 0.3s, border-radius 0.3s',
                }
          }>
            {filteredBlocks.length === 0 && (
              <div style={{
                position: 'absolute',
                top: '40%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#aaa',
                fontSize: 24,
                textAlign: 'center',
                pointerEvents: 'none'
              }}>
                Click a button above to add your first block!
              </div>
            )}
            {/* Dynamic price context provider for this tab/layout */}
            <SelectedWeightContext.Provider value={selectedWeightState}>
              {isPreviewMode ? (
                <BlockRenderer blocks={filteredBlocks} />
              ) : (
                filteredBlocks.map((block, i) => {
                  try {
                    if (!block || typeof block !== 'object' || !block.type) return null;
                    // Find the first weightSelector block in this tab
                    const weightSelectorBlock = filteredBlocks.find(b => b.type === 'button' && b.buttonType === 'weightSelector');
                    return (
                      <Rnd
                        key={block.id}
                        size={{ width: block.width, height: block.height }}
                        position={{ x: block.x, y: block.y }}
                        onDragStop={(e, d) => handleBlockChange(block.id, { x: d.x, y: d.y })}
                        onResizeStop={(e, dir, ref, delta, pos) =>
                          handleBlockChange(block.id, {
                            width: ref.offsetWidth,
                            height: ref.offsetHeight,
                            x: pos.x,
                            y: pos.y,
                          })
                        }
                        style={{
                          zIndex: block.id === selectedBlockId ? 10 : 2,
                          transform: `rotate(${block.rotation || 0}deg)`,
                          border: block.id === selectedBlockId ? '2px solid #a259ff' : 'none',
                          boxShadow: block.id === selectedBlockId ? '0 0 0 1.5px #a259ff' : 'none',
                        }}
                        onClick={e => { e.stopPropagation(); setSelectedBlockId(block.id); }}
                        enableResizing={true}
                        disableDragging={false}
                        resizeHandleComponent={renderHandles(block.id === selectedBlockId)}
                      >
                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                          {/* Only show delete/rotate handles if selected */}
                          {block.id === selectedBlockId && (
                            <>
                              <button
                                onClick={e => { e.stopPropagation(); handleDeleteBlock(block.id); }}
                                style={{ position: 'absolute', top: -18, right: -18, background: '#e00', color: '#fff', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', zIndex: 20 }}
                                title="Delete block"
                              >🗑️</button>
                              <div
                                style={{ ...handleStyles.rotate }}
                                onMouseDown={e => {
                                  e.stopPropagation();
                                  handleRotateBlock(block.id, 15);
                                }}
                                title="Rotate"
                              >
                                <span style={{ fontSize: 22, color: '#a259ff' }}>⟳</span>
                              </div>
                            </>
                          )}
                          {/* Block content rendering (text, image, button, display, etc.) */}
                          {block.type === 'text' ? (
                            <div
                              style={{
                                fontSize: block.fontSize,
                                fontWeight: block.fontWeight,
                                color: normalizeHexColor(block.color || '#111'),
                                fontFamily: block.fontFamily,
                                letterSpacing: block.letterSpacing,
                                lineHeight: block.lineHeight,
                                textAlign: block.textAlign,
                                textShadow: block.textShadow,
                                outline: block.outline,
                                background: 'transparent',
                                padding: 4,
                                borderRadius: 4,
                                minWidth: 40,
                                width: '100%',
                                height: '100%',
                                cursor: 'pointer',
                                whiteSpace: block.wrapText === false ? 'nowrap' : 'pre-line',
                                overflow: 'hidden',
                              }}
                            >
                              {block.content}
                            </div>
                          ) : block.type === 'image' ? (
                            <img
                              src={block.src}
                              alt=""
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                borderRadius: block.borderRadius,
                                boxShadow: block.boxShadow,
                                outline: block.outline,
                                opacity: block.opacity,
                                cursor: 'pointer',
                              }}
                            />
                          ) : block.type === 'button' && block.buttonType === 'weightSelector' ? (
                            <WeightSelectorBlock block={block} />
                          ) : block.type === 'button' && block.buttonType === 'typeSelector' ? (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                background: block.noBackground ? 'transparent' : block.background,
                                borderRadius: block.borderRadius,
                                boxShadow: block.boxShadow,
                                outline: block.outline,
                                fontSize: block.fontSize,
                                fontWeight: block.fontWeight,
                                color: block.color,
                              }}
                            >
                              <span>{block.label || 'Type'}:</span>
                              {Array.isArray(block.options) && block.options.length > 0 && block.options.map((opt, idx) => {
                                const buttonRadius = typeof block.buttonRadius === 'number' ? block.buttonRadius : 16;
                                return (
                                  <div key={opt.type || idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                    {/* Editable type field */}
                                    <input
                                      type="text"
                                      value={opt.type}
                                      placeholder="Type value (e.g. filter)"
                                      onChange={e => {
                                        const newOptions = [...block.options];
                                        newOptions[idx] = { ...newOptions[idx], type: e.target.value };
                                        handlePropChange('options', newOptions);
                                      }}
                                      style={{ width: 100 }}
                                      required
                                    />
                                    {/* Editable label field */}
                                    <input
                                      type="text"
                                      value={opt.label}
                                      placeholder="Type label (e.g. Filter)"
                                      onChange={e => {
                                        const newOptions = [...block.options];
                                        // If type is empty, auto-fill it with lowercased label
                                        newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                                        if (!newOptions[idx].type) {
                                          newOptions[idx].type = e.target.value.toLowerCase();
                                        }
                                        handlePropChange('options', newOptions);
                                      }}
                                      style={{ width: 100 }}
                                      required
                                    />
                                    <input
                                      type="checkbox"
                                      checked={!!opt.available}
                                      onChange={e => {
                                        const newOptions = [...block.options];
                                        newOptions[idx] = { ...newOptions[idx], available: e.target.checked };
                                        handlePropChange('options', newOptions);
                                      }}
                                    />
                                    <input
                                      type="radio"
                                      name="defaultType"
                                      checked={!!opt.default}
                                      onChange={() => {
                                        const newOptions = block.options.map((o, i) => ({ ...o, default: i === idx }));
                                        handlePropChange('options', newOptions);
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        const newOptions = block.options.filter((_, i) => i !== idx);
                                        handlePropChange('options', newOptions);
                                      }}
                                      style={{ color: '#e00', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}
                                      title="Remove"
                                    >✕</button>
                                  </div>
                                );
                              })}
                            </div>
                          ) : block.type === 'button' ? (
                            <button
                              style={{
                                width: '100%',
                                height: '100%',
                                fontSize: block.fontSize,
                                fontWeight: block.fontWeight,
                                color: block.color,
                                background: block.background,
                                borderRadius: block.borderRadius,
                                boxShadow: block.boxShadow,
                                outline: block.outline,
                                border: 'none',
                                cursor: 'pointer',
                              }}
                            >
                              {block.content}
                            </button>
                          ) : block.type === 'display' && weightSelectorBlock ? (
                            <DynamicPriceDisplayBlock block={block} weightSelectorBlock={weightSelectorBlock} />
                          ) : block.type === 'display' ? (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                fontSize: block.fontSize,
                                fontWeight: block.fontWeight,
                                color: block.color,
                                background: block.background,
                                borderRadius: block.borderRadius,
                                boxShadow: block.boxShadow,
                                outline: block.outline,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              {block.displayType === 'quantity' ? block.label + ': 1'
                                : block.displayType === 'grams' ? `${block.label || 'Weight'}: 250${block.unit || 'g'}`
                                : block.displayType === 'beanType' ? `${block.label || 'Type'}: Filter` : ''}
                            </div>
                          ) : block.type === 'video' ? (
                            <video
                              key={block.id}
                              src={block.src}
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: block.borderRadius,
                                boxShadow: block.boxShadow,
                                outline: block.outline,
                                opacity: block.opacity,
                                cursor: 'pointer',
                                objectFit: 'contain',
                              }}
                              controls
                              loop
                              autoPlay
                              muted
                            />
                          ) : block.type === 'pricing' ? (
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                fontSize: block.fontSize,
                                fontWeight: block.fontWeight,
                                color: block.color,
                                background: block.background,
                                borderRadius: block.borderRadius,
                                boxShadow: block.boxShadow,
                                outline: block.outline,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                            >
                              Total: {block.totalPrice} QAR
                            </div>
                          ) : null}
                        </div>
                      </Rnd>
                    );
                  } catch (err) {
                    console.error('Error rendering block at index', i, block, err);
                    return null;
                  }
                })
              )}
            </SelectedWeightContext.Provider>
            <button onClick={handleSave} style={{ position: 'absolute', right: 16, bottom: 16, zIndex: 10 }}>
              Save
            </button>
            {/* Floating Properties Panel */}
            {selectedBlock && (
              <div
                style={{
                  position: 'fixed',
                  top: 80,
                  right: 0,
                  width: 340,
                  background: '#fff',
                  borderLeft: '2px solid #eee',
                  boxShadow: '-2px 0 12px rgba(0,0,0,0.06)',
                  padding: 24,
                  zIndex: 1000,
                  height: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
              >
                {/* Close button */}
                <button
                  onClick={() => setSelectedBlockId(null)}
                  style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', zIndex: 1001 }}
                  title="Close"
                >✕</button>
                <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>
                  Edit {selectedBlock.type.charAt(0).toUpperCase() + selectedBlock.type.slice(1)}
                </div>
                {/* Block reordering controls */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <button
                    onClick={() => {
                      const idx = filteredBlocks.findIndex(b => b.id === selectedBlock.id);
                      if (idx > 0) {
                        const newBlocks = [...filteredBlocks];
                        [newBlocks[idx - 1], newBlocks[idx]] = [newBlocks[idx], newBlocks[idx - 1]];
                        updateBlocks(newBlocks);
                      }
                    }}
                    disabled={filteredBlocks.findIndex(b => b.id === selectedBlock.id) === 0}
                    style={{ padding: '4px 10px', fontSize: 14, border: '1px solid #aaa', borderRadius: 4, background: '#f7f7f7', cursor: 'pointer' }}
                    title="Send Backward"
                  >⬅ Back</button>
                  <button
                    onClick={() => {
                      const idx = filteredBlocks.findIndex(b => b.id === selectedBlock.id);
                      if (idx < filteredBlocks.length - 1) {
                        const newBlocks = [...filteredBlocks];
                        [newBlocks[idx], newBlocks[idx + 1]] = [newBlocks[idx + 1], newBlocks[idx]];
                        updateBlocks(newBlocks);
                      }
                    }}
                    disabled={filteredBlocks.findIndex(b => b.id === selectedBlock.id) === filteredBlocks.length - 1}
                    style={{ padding: '4px 10px', fontSize: 14, border: '1px solid #aaa', borderRadius: 4, background: '#f7f7f7', cursor: 'pointer' }}
                    title="Bring Forward"
                  >Forward ➡</button>
                </div>
                {/* Common: Rotation */}
                <label>Rotation (deg)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input
                    type="number"
                    value={selectedBlock.rotation || 0}
                    onChange={e => handlePropChange('rotation', Number(e.target.value))}
                    style={{ width: 80 }}
                  />
                  <button
                    onClick={() => handlePropChange('rotation', 0)}
                    style={{ padding: '2px 10px', fontSize: 14, border: '1px solid #aaa', borderRadius: 4, background: '#f7f7f7', cursor: 'pointer' }}
                  >Reset</button>
                </div>
                {/* Text block controls */}
                {selectedBlock.type === 'text' && (
                  <>
                    <label>Text</label>
                    <textarea
                      value={selectedBlock.content}
                      onChange={e => handlePropChange('content', e.target.value)}
                      style={{ width: '100%', minHeight: 40, fontSize: 16, marginBottom: 8 }}
                    />
                    <label>Font Family</label>
                    <select
                      value={selectedBlock.fontFamily}
                      onChange={e => handlePropChange('fontFamily', e.target.value)}
                      style={{ width: '100%', marginBottom: 8 }}
                    >
                      {FONT_FAMILIES.map(f => (
                        <option key={f} value={f}>{f.split(',')[0]}</option>
                      ))}
                    </select>
                    <label>Font Size</label>
                    <input
                      type="number"
                      value={selectedBlock.fontSize}
                      onChange={e => handlePropChange('fontSize', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Font Weight</label>
                    <select
                      value={selectedBlock.fontWeight}
                      onChange={e => handlePropChange('fontWeight', e.target.value)}
                      style={{ width: 100, marginBottom: 8 }}
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value={700}>700</option>
                      <option value={900}>900</option>
                    </select>
                    <label>Color</label>
                    <input
                      type="color"
                      value={normalizeHexColor(selectedBlock.color || '#111')}
                      onChange={e => handlePropChange('color', e.target.value)}
                      style={{ width: 40, height: 32, marginBottom: 8, border: 'none', background: 'none' }}
                    />
                    <label>Letter Spacing</label>
                    <input
                      type="number"
                      value={selectedBlock.letterSpacing}
                      onChange={e => handlePropChange('letterSpacing', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Line Height</label>
                    <input
                      type="number"
                      value={selectedBlock.lineHeight}
                      onChange={e => handlePropChange('lineHeight', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Text Align</label>
                    <select
                      value={selectedBlock.textAlign}
                      onChange={e => handlePropChange('textAlign', e.target.value)}
                      style={{ width: 100, marginBottom: 8 }}
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={selectedBlock.wrapText !== false}
                        onChange={e => handlePropChange('wrapText', e.target.checked)}
                        style={{ marginRight: 6 }}
                      />
                      Wrap Text
                    </label>
                    <label>Text Shadow</label>
                    <input
                      type="text"
                      value={selectedBlock.textShadow}
                      onChange={e => handlePropChange('textShadow', e.target.value)}
                      placeholder="e.g. 1px 1px 2px #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label>Outline</label>
                    <input
                      type="text"
                      value={selectedBlock.outline}
                      onChange={e => handlePropChange('outline', e.target.value)}
                      placeholder="e.g. 1px solid #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!selectedBlock.isDynamicPrice}
                        onChange={e => handlePropChange('isDynamicPrice', e.target.checked)}
                        style={{ marginRight: 6 }}
                      />
                      Dynamic Price
                    </label>
                  </>
                )}
                {/* Image block controls */}
                {selectedBlock.type === 'image' && (
                  <>
                    <label>Image URL</label>
                    <input
                      type="text"
                      value={selectedBlock.src || ''}
                      onChange={e => handlePropChange('src', e.target.value)}
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label>Border Radius</label>
                    <input
                      type="number"
                      value={selectedBlock.borderRadius || 0}
                      onChange={e => handlePropChange('borderRadius', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Box Shadow</label>
                    <input
                      type="text"
                      value={selectedBlock.boxShadow || ''}
                      onChange={e => handlePropChange('boxShadow', e.target.value)}
                      placeholder="e.g. 2px 2px 8px #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label>Outline</label>
                    <input
                      type="text"
                      value={selectedBlock.outline || ''}
                      onChange={e => handlePropChange('outline', e.target.value)}
                      placeholder="e.g. 1px solid #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label>Opacity (Transparency)</label>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.01}
                      value={selectedBlock.opacity ?? 1}
                      onChange={e => handlePropChange('opacity', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                  </>
                )}
                {/* Button block controls */}
                {selectedBlock.type === 'button' && (
                  <>
                    {/* If selector block, show selector-specific controls */}
                    {(selectedBlock.buttonType === 'weightSelector' || selectedBlock.buttonType === 'typeSelector') && (
                      <>
                        {/* Options Editor */}
                        <label>{selectedBlock.buttonType === 'weightSelector' ? 'Weights & Prices:' : 'Types'}:</label>
                        <div style={{ marginBottom: 8 }}>
                          {(Array.isArray(selectedBlock.options) ? selectedBlock.options : []).map((opt, idx) => (
                            <div key={opt.type || idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                              <input
                                type="text"
                                value={opt.type}
                                placeholder="Type value (e.g. filter)"
                                onChange={e => {
                                  const newOptions = [...selectedBlock.options];
                                  newOptions[idx] = { ...newOptions[idx], type: e.target.value };
                                  handlePropChange('options', newOptions);
                                }}
                                style={{ width: 100 }}
                                required
                              />
                              <input
                                type="text"
                                value={opt.label}
                                placeholder="Type label (e.g. Filter)"
                                onChange={e => {
                                  const newOptions = [...selectedBlock.options];
                                  // If type is empty, auto-fill it with lowercased label
                                  newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                                  if (!newOptions[idx].type) {
                                    newOptions[idx].type = e.target.value.toLowerCase();
                                  }
                                  handlePropChange('options', newOptions);
                                }}
                                style={{ width: 100 }}
                                required
                              />
                              <input
                                type="checkbox"
                                checked={!!opt.available}
                                onChange={e => {
                                  const newOptions = [...selectedBlock.options];
                                  newOptions[idx] = { ...newOptions[idx], available: e.target.checked };
                                  handlePropChange('options', newOptions);
                                }}
                              />
                              <input
                                type="radio"
                                name="defaultType"
                                checked={!!opt.default}
                                onChange={() => {
                                  const newOptions = selectedBlock.options.map((o, i) => ({ ...o, default: i === idx }));
                                  handlePropChange('options', newOptions);
                                }}
                              />
                              <button
                                onClick={() => {
                                  const newOptions = selectedBlock.options.filter((_, i) => i !== idx);
                                  handlePropChange('options', newOptions);
                                }}
                                style={{ color: '#e00', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}
                                title="Remove"
                              >✕</button>
                            </div>
                          ))}
                          <button
                            onClick={() => {
                              const newOptions = [
                                ...selectedBlock.options,
                                { type: '', label: '', available: true, default: false }
                              ];
                              handlePropChange('options', newOptions);
                            }}
                            style={{ fontSize: 14, padding: '2px 10px', border: '1px solid #aaa', borderRadius: 4, background: '#f7f7f7', cursor: 'pointer', marginTop: 2 }}
                          >Add Type</button>
                        </div>
                        {/* Dynamic Price Editor for weightSelector */}
                        {selectedBlock.buttonType === 'weightSelector' && (
                          <div style={{ marginBottom: 8 }}>
                            <label>Dynamic Prices (per weight):</label>
                            {(selectedBlock.options || []).map((opt, idx) => (
                              <div key={opt.weight} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <span style={{ minWidth: 40 }}>{opt.weight}{selectedBlock.unit}</span>
                                <input
                                  type="number"
                                  value={selectedBlock.prices?.[opt.weight] || ''}
                                  onChange={e => {
                                    const newPrices = { ...(selectedBlock.prices || {}) };
                                    newPrices[opt.weight] = e.target.value;
                                    handlePropChange('prices', newPrices);
                                  }}
                                  placeholder="Price"
                                  style={{ width: 80 }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        <label>Button Radius</label>
                        <input
                          type="number"
                          value={selectedBlock.buttonRadius ?? 16}
                          onChange={e => handlePropChange('buttonRadius', Number(e.target.value))}
                          style={{ width: 80, marginBottom: 8 }}
                        />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <input
                            type="checkbox"
                            checked={!!selectedBlock.noButtonBackground}
                            onChange={e => handlePropChange('noButtonBackground', e.target.checked)}
                            style={{ marginRight: 6 }}
                          />
                          No Background (Selector Buttons)
                        </label>
                      </>
                    )}
                    <label>Font Size</label>
                    <input
                      type="number"
                      value={selectedBlock.fontSize}
                      onChange={e => handlePropChange('fontSize', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Font Weight</label>
                    <select
                      value={selectedBlock.fontWeight}
                      onChange={e => handlePropChange('fontWeight', e.target.value)}
                      style={{ width: 100, marginBottom: 8 }}
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value={700}>700</option>
                      <option value={900}>900</option>
                    </select>
                    <label>Text Color</label>
                    <input
                      type="color"
                      value={normalizeHexColor(selectedBlock.color || '#111')}
                      onChange={e => handlePropChange('color', e.target.value)}
                      style={{ width: 40, height: 32, marginBottom: 8, border: 'none', background: 'none' }}
                    />
                    <label>Background</label>
                    <input
                      type="color"
                      value={normalizeHexColor(selectedBlock.background || '#fff')}
                      onChange={e => handlePropChange('background', e.target.value)}
                      style={{ width: 40, height: 32, marginBottom: 8, border: 'none', background: 'none' }}
                    />
                    <label>Border Radius</label>
                    <input
                      type="number"
                      value={selectedBlock.borderRadius || 0}
                      onChange={e => handlePropChange('borderRadius', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Box Shadow</label>
                    <input
                      type="text"
                      value={selectedBlock.boxShadow || ''}
                      onChange={e => handlePropChange('boxShadow', e.target.value)}
                      placeholder="e.g. 2px 2px 8px #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label>Outline</label>
                    <input
                      type="text"
                      value={selectedBlock.outline || ''}
                      onChange={e => handlePropChange('outline', e.target.value)}
                      placeholder="e.g. 1px solid #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                  </>
                )}
                {/* Display block controls */}
                {selectedBlock.type === 'display' && (
                  <>
                    <label>Display Type</label>
                    <select
                      value={selectedBlock.displayType || 'quantity'}
                      onChange={e => handlePropChange('displayType', e.target.value)}
                      style={{ width: '100%', marginBottom: 8 }}
                    >
                      <option value="quantity">Quantity</option>
                      <option value="grams">Grams</option>
                      <option value="beanType">Bean Type</option>
                    </select>
                    <label>Label</label>
                    <input
                      type="text"
                      value={selectedBlock.label}
                      onChange={e => handlePropChange('label', e.target.value)}
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    {selectedBlock.displayType === 'grams' && (
                      <>
                        <label>Unit</label>
                        <input
                          type="text"
                          value={selectedBlock.unit}
                          onChange={e => handlePropChange('unit', e.target.value)}
                          style={{ width: 80, marginBottom: 8 }}
                        />
                        <label>Grams per Unit</label>
                        <input
                          type="number"
                          value={selectedBlock.gramsPerUnit}
                          onChange={e => handlePropChange('gramsPerUnit', Number(e.target.value))}
                          style={{ width: 80, marginBottom: 8 }}
                        />
                      </>
                    )}
                    {/* Style controls as before */}
                    <label>Font Size</label>
                    <input
                      type="number"
                      value={selectedBlock.fontSize}
                      onChange={e => handlePropChange('fontSize', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Font Weight</label>
                    <select
                      value={selectedBlock.fontWeight}
                      onChange={e => handlePropChange('fontWeight', e.target.value)}
                      style={{ width: 100, marginBottom: 8 }}
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value={700}>700</option>
                      <option value={900}>900</option>
                    </select>
                    <label>Text Color</label>
                    <input
                      type="color"
                      value={normalizeHexColor(selectedBlock.color || '#111')}
                      onChange={e => handlePropChange('color', e.target.value)}
                      style={{ width: 40, height: 32, marginBottom: 8, border: 'none', background: 'none' }}
                    />
                    <label>Background</label>
                    <input
                      type="color"
                      value={normalizeHexColor(selectedBlock.background || '#fff')}
                      onChange={e => handlePropChange('background', e.target.value)}
                      style={{ width: 40, height: 32, marginBottom: 8, border: 'none', background: 'none' }}
                    />
                    <label>Border Radius</label>
                    <input
                      type="number"
                      value={selectedBlock.borderRadius || 0}
                      onChange={e => handlePropChange('borderRadius', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Box Shadow</label>
                    <input
                      type="text"
                      value={selectedBlock.boxShadow || ''}
                      onChange={e => handlePropChange('boxShadow', e.target.value)}
                      placeholder="e.g. 2px 2px 8px #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label>Outline</label>
                    <input
                      type="text"
                      value={selectedBlock.outline || ''}
                      onChange={e => handlePropChange('outline', e.target.value)}
                      placeholder="e.g. 1px solid #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                  </>
                )}
                {/* Video block properties */}
                {selectedBlock.type === 'video' && (
                  <>
                    <label>Video URL</label>
                    <input
                      type="text"
                      value={selectedBlock.src || ''}
                      onChange={e => handlePropChange('src', e.target.value)}
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                  </>
                )}
                {/* Copy/Paste controls */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <button onClick={handleCopyBlock} style={{ padding: '4px 10px', fontSize: 14, border: '1px solid #aaa', borderRadius: 4, background: '#f7f7f7', cursor: 'pointer' }}>Copy</button>
                  {clipboard && (
                    <button onClick={handlePasteBlock} style={{ padding: '4px 10px', fontSize: 14, border: '1px solid #aaa', borderRadius: 4, background: '#f7f7f7', cursor: 'pointer' }}>Paste</button>
                  )}
                </div>
                {/* Validation: unique weights, numeric price */}
                {(() => {
                  const weights = (selectedBlock.options || []).map(o => o.weight);
                  const hasDuplicates = new Set(weights).size !== weights.length;
                  const hasInvalid = (selectedBlock.options || []).some(o => !o.weight || o.price < 0 || isNaN(o.price));
                  if (hasDuplicates) return <div style={{ color: '#e00', fontWeight: 600 }}>Duplicate weights not allowed.</div>;
                  if (hasInvalid) return <div style={{ color: '#e00', fontWeight: 600 }}>All weights and prices must be valid numbers.</div>;
                  return null;
                })()}
                {selectedBlock.type === 'pricing' && (
                  <>
                    <label>Font Family</label>
                    <select
                      value={selectedBlock.fontFamily || 'Inter, Arial, sans-serif'}
                      onChange={e => handlePropChange('fontFamily', e.target.value)}
                      style={{ width: '100%', marginBottom: 8 }}
                    >
                      {FONT_FAMILIES.map(f => (
                        <option key={f} value={f}>{f.split(',')[0]}</option>
                      ))}
                    </select>
                    <label>Font Size</label>
                    <input
                      type="number"
                      value={selectedBlock.fontSize}
                      onChange={e => handlePropChange('fontSize', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Font Weight</label>
                    <select
                      value={selectedBlock.fontWeight}
                      onChange={e => handlePropChange('fontWeight', e.target.value)}
                      style={{ width: 100, marginBottom: 8 }}
                    >
                      <option value="normal">Normal</option>
                      <option value="bold">Bold</option>
                      <option value={700}>700</option>
                      <option value={900}>900</option>
                    </select>
                    <label>Text Color</label>
                    <input
                      type="color"
                      value={normalizeHexColor(selectedBlock.color || '#111')}
                      onChange={e => handlePropChange('color', e.target.value)}
                      style={{ width: 40, height: 32, marginBottom: 8, border: 'none', background: 'none' }}
                    />
                    <label>Background</label>
                    <input
                      type="color"
                      value={normalizeHexColor(selectedBlock.background || '#fff')}
                      onChange={e => handlePropChange('background', e.target.value)}
                      style={{ width: 40, height: 32, marginBottom: 8, border: 'none', background: 'none' }}
                    />
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!selectedBlock.noBackground}
                        onChange={e => handlePropChange('noBackground', e.target.checked)}
                        style={{ marginRight: 6 }}
                      />
                      No Background
                    </label>
                    <label>Border Radius</label>
                    <input
                      type="number"
                      value={selectedBlock.borderRadius || 0}
                      onChange={e => handlePropChange('borderRadius', Number(e.target.value))}
                      style={{ width: 80, marginBottom: 8 }}
                    />
                    <label>Box Shadow</label>
                    <input
                      type="text"
                      value={selectedBlock.boxShadow || ''}
                      onChange={e => handlePropChange('boxShadow', e.target.value)}
                      placeholder="e.g. 2px 2px 8px #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                    <label>Outline</label>
                    <input
                      type="text"
                      value={selectedBlock.outline || ''}
                      onChange={e => handlePropChange('outline', e.target.value)}
                      placeholder="e.g. 1px solid #000"
                      style={{ width: '100%', marginBottom: 8 }}
                    />
                  </>
                )}
                {selectedBlock.type === 'button' && selectedBlock.buttonType === 'typeSelector' && (
                  <>
                    <label>Types:</label>
                    <div style={{ marginBottom: 8 }}>
                      {(Array.isArray(selectedBlock.options) ? selectedBlock.options : []).map((opt, idx) => (
                        <div key={opt.type || idx} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <input
                            type="text"
                            value={opt.label}
                            placeholder="Type label (e.g. Filter)"
                            onChange={e => {
                              const newOptions = [...selectedBlock.options];
                              newOptions[idx] = { ...newOptions[idx], label: e.target.value };
                              handlePropChange('options', newOptions);
                            }}
                            style={{ width: 100 }}
                            required
                          />
                          <input
                            type="checkbox"
                            checked={!!opt.available}
                            onChange={e => {
                              const newOptions = [...selectedBlock.options];
                              newOptions[idx] = { ...newOptions[idx], available: e.target.checked };
                              handlePropChange('options', newOptions);
                            }}
                          />
                          <input
                            type="radio"
                            name="defaultType"
                            checked={!!opt.default}
                            onChange={() => {
                              const newOptions = selectedBlock.options.map((o, i) => ({ ...o, default: i === idx }));
                              handlePropChange('options', newOptions);
                            }}
                          />
                          <button
                            onClick={() => {
                              const newOptions = selectedBlock.options.filter((_, i) => i !== idx);
                              handlePropChange('options', newOptions);
                            }}
                            style={{ color: '#e00', background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}
                            title="Remove"
                          >✕</button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newOptions = [
                            ...selectedBlock.options,
                            { type: '', label: '', available: true, default: false }
                          ];
                          handlePropChange('options', newOptions);
                        }}
                        style={{ fontSize: 14, padding: '2px 10px', border: '1px solid #aaa', borderRadius: 4, background: '#f7f7f7', cursor: 'pointer', marginTop: 2 }}
                      >Add Type</button>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <input
                        type="checkbox"
                        checked={!!selectedBlock.noBackground}
                        onChange={e => handlePropChange('noBackground', e.target.checked)}
                        style={{ marginRight: 6 }}
                      />
                      No Background
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default ProductEditorCanvas; 